import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import uppoliceLogo from '../images/UPPOLICE_LOGO.png';
import {
  FaShieldAlt,
  FaSignInAlt,
  FaLock,
  FaUser,
  FaEye,
  FaEyeSlash,
  FaTimes,
  FaExclamationTriangle,
  FaFileAlt,
  FaVideo,
  FaUserCheck,
  FaPlayCircle,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaInfoCircle,
  FaCogs,
  FaLaptopCode,
  FaDatabase,
  FaClock,
  FaEnvelope,
  FaCode,
  FaPhone,
} from 'react-icons/fa';

/**
 * Upgradeable Department Portals Configuration
 * To update or add new department portals, simply modify this array:
 */
const DEPARTMENT_PORTALS = [
  {
    id: 'online-gd',
    name: 'Online GD Portal',
    url: 'https://onlinegd.copsuvidha.com',
    subtext: 'General Diary System',
    badge: 'Active',
    badgeType: 'active',
    icon: FaFileAlt,
    disabled: false,
    action: 'link', // Opens external link in new tab
  },
  {
    id: 'esakshya',
    name: 'eSakshya Portal',
    url: 'https://esakshya.copsuvidha.com',
    subtext: 'Monitoring & Analytics',
    badge: 'Active',
    badgeType: 'active',
    icon: FaVideo,
    disabled: false,
    action: 'link', // Opens external link in new tab
  },
  {
    id: 'verification',
    name: 'Verification',
    url: 'https://citiserv.copsuvidha.com',
    subtext: 'Citizen Service Monitoring & Analytics',
    badge: 'Active',
    badgeType: 'active',
    icon: FaUserCheck,
    disabled: false,
    action: 'link', // Opens external link in new tab
  },
  {
    id: 'coptube',
    name: 'CopTube',
    url: '#',
    subtext: 'Department Training & Archive Videos',
    badge: 'Coming Soon',
    badgeType: 'soon',
    icon: FaPlayCircle,
    disabled: true, // Link disabled
    action: 'disabled',
  },
];

export default function MainLandingPage() {
  const { login } = useAuth();
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const triggerToast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const handlePortalClick = (portal, e) => {
    if (portal.disabled) {
      e.preventDefault();
      triggerToast('CopTube is coming soon! Police video training and archive module is currently under development.');
      return;
    }

    if (portal.action === 'login') {
      e.preventDefault();
      setIsLoginOpen(true);
      return;
    }

    // Default external links open in new tab via href & target="_blank"
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password.');
      return;
    }

    setLoading(true);
    try {
      await login(username.trim(), password);
      // Once logged in, AuthContext automatically triggers DashboardPage
    } catch (err) {
      setError(err.message || 'Login failed. Please verify credentials.');
    } finally {
      setLoading(false);
    }
  };

  const setDemoCredentials = (u, p) => {
    setUsername(u);
    setPassword(p);
  };

  return (
    <div className="cop-landing-root">
      {/* Top Header Navigation */}
      <header className="cop-top-nav">
        <div className="cop-nav-brand">
          <img src={uppoliceLogo} alt="UP Police Emblem" className="cop-nav-logo" />
          <div>
            <h1 className="cop-nav-brand-title">COP SUVIDHA</h1>
            <span className="cop-nav-brand-sub">Police Digital Services Platform</span>
          </div>
        </div>

        <div className="cop-nav-actions">
          {/* <div className="cop-status-badge">
            <span className="cop-status-dot" />
            <span>All Department Portals Operational</span>
          </div> */}

          <button
            type="button"
            className="cop-officer-btn"
            onClick={() => setIsLoginOpen(true)}
            id="officer-login-btn"
            title="Sign in to Police Role Management"
          >
            <FaSignInAlt />
            <span>User Login</span>
          </button>
        </div>
      </header>

      {/* Hero Section with Glassmorphism Card */}
      <main className="cop-hero-wrap">
        <div className="cop-glass-hero">
          <h2 className="cop-hero-title">COP SUVIDHA</h2>

          <p className="cop-hero-desc">
            A digital support platform developed to assist police personnel in managing office work,
            reporting, evidence handling, documentation, and administrative tasks with greater speed,
            accuracy, and automation.
          </p>

          {/* Inner Portal Box */}
          <div className="cop-portal-wrapper">
            <div className="cop-portal-title">Access Department Portals</div>

            <div className="cop-portal-grid">
              {DEPARTMENT_PORTALS.map((portal) => {
                const IconComponent = portal.icon;
                const isComingSoon = portal.disabled;

                return (
                  <a
                    key={portal.id}
                    href={portal.url}
                    target={portal.action === 'link' ? '_blank' : undefined}
                    rel={portal.action === 'link' ? 'noopener noreferrer' : undefined}
                    onClick={(e) => handlePortalClick(portal, e)}
                    className={`cop-portal-card ${isComingSoon ? 'cop-portal-disabled' : ''}`}
                    title={
                      isComingSoon
                        ? 'CopTube — Coming Soon (Disabled)'
                        : portal.action === 'login'
                        ? 'Click to open Verification / Officer Access'
                        : `Open ${portal.name} in new tab`
                    }
                    aria-disabled={isComingSoon}
                  >
                    {/* Badge */}
                    <span
                      className={`cop-portal-badge ${
                        portal.badgeType === 'active'
                          ? 'cop-badge-active'
                          : portal.badgeType === 'login'
                          ? 'cop-badge-login'
                          : 'cop-badge-soon'
                      }`}
                    >
                      {portal.badge}
                    </span>

                    {/* Icon */}
                    <div className="cop-portal-icon-wrap">
                      <IconComponent />
                    </div>

                    {/* Titles */}
                    <div className="cop-portal-name">
                      {portal.name}
                      {portal.action === 'link' && (
                        <FaExternalLinkAlt style={{ fontSize: '0.68rem', marginLeft: '6px', opacity: 0.6 }} />
                      )}
                    </div>

                    <div className="cop-portal-sub">{portal.subtext}</div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      {/* Advanced Capabilities / Technology Highlights Section */}
      <section className="cop-features-section">
        <div className="cop-section-header">
          <h2 className="cop-section-title">Supporting Police Personnel Through Technology</h2>
          <p className="cop-section-desc">
            COP Suvidha enables police staff to reduce manual paperwork and simplify digital workflows.
            The platform supports operational efficiency, record management, reporting systems, and
            technology-driven office administration.
          </p>
        </div>

      </section>

      {/* Contact Us — Horizontal Card */}
      <section className="cop-contact-section">
        <div className="cop-contact-card">
          {/* Left label */}
          <div className="cop-contact-card-label">
            <FaEnvelope />
            <span>Contact Us</span>
          </div>

          <span className="cop-contact-card-divider" />

          {/* Info chips */}
          <div className="cop-contact-card-chip">
            <FaShieldAlt className="cop-contact-chip-icon" />
            <div>
              <span className="cop-contact-chip-sub">Developed By</span>
              <span className="cop-contact-chip-val">Agra Police</span>
            </div>
          </div>

          <span className="cop-contact-card-divider" />

          <div className="cop-contact-card-chip">
            <FaUser className="cop-contact-chip-icon" />
            <div>
              <span className="cop-contact-chip-sub">Developer</span>
              <span className="cop-contact-chip-val">Sonu Rawal</span>
              <span className="cop-contact-chip-designation">Computer Operator Grade A <br/>District Coordinator, <br/> West Zone Comm. Agra</span>
            </div>
          </div>

          <span className="cop-contact-card-divider" />

          <div className="cop-contact-card-chip">
            <FaEnvelope className="cop-contact-chip-icon" />
            <div>
              <span className="cop-contact-chip-sub">Email</span>
              <a href="mailto:skrawal007@gmail.com" className="cop-contact-chip-link">skrawal007@gmail.com</a>
            </div>
          </div>

          <span className="cop-contact-card-divider" />

          <div className="cop-contact-card-chip">
            <FaPhone className="cop-contact-chip-icon" />
            <div>
              <span className="cop-contact-chip-sub">Mobile</span>
              <a href="tel:8077709902" className="cop-contact-chip-link">8077709902</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="cop-footer">
        <p>© 2026 COP Suvidha — Developed by Agra Police &nbsp;|&nbsp; <strong>Sonu Rawal</strong></p>
      </footer>

      {/* Floating Toast Message for CopTube / Notifications */}
      {showToast && (
        <div className="cop-toast" role="status">
          <FaInfoCircle style={{ color: '#38bdf8', fontSize: '1.1rem' }} />
          <span>{toastMessage}</span>
          <button
            type="button"
            onClick={() => setShowToast(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              marginLeft: '8px',
            }}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Officer Login / Verification Modal */}
      {isLoginOpen && (
        <div className="modal-overlay" onClick={() => setIsLoginOpen(false)}>
          <div
            className="modal-container"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '440px', background: '#ffffff', borderRadius: '16px' }}
          >
            <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#eff6ff',
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                  }}
                >
                  <FaShieldAlt />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.18rem', color: '#0f172a', margin: 0, fontWeight: 700 }}>
                    User Managment Login
                  </h3>
                  <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    COP SUVIDHA — Role-Based Access
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsLoginOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                }}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleLoginSubmit}>
              <div className="modal-body" style={{ padding: '24px' }}>
                {error && (
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: '#fef2f2',
                      border: '1px solid #fca5a5',
                      color: '#991b1b',
                      fontSize: '0.84rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                    }}
                  >
                    <FaExclamationTriangle />
                    <span>{error}</span>
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.84rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    <FaUser style={{ color: '#1e40af' }} />
                    <span>Username / Official ID</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. admin or 9454401009"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '22px' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.84rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    <FaLock style={{ color: '#1e40af' }} />
                    <span>Password</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      placeholder="Enter account password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#64748b',
                        cursor: 'pointer',
                        fontSize: '1rem',
                      }}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '0.96rem',
                    borderRadius: '8px',
                    fontWeight: 700,
                  }}
                  disabled={loading}
                >
                  <FaSignInAlt />
                  <span>{loading ? 'Authenticating...' : 'Sign In to Portal'}</span>
                </button>

                {/* Quick Demo Credentials for Fast Testing */}
                {/* <div
                  style={{
                    marginTop: '20px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #cbd5e1',
                    fontSize: '0.78rem',
                    color: '#475569',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>
                    Quick Demo Credentials:
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setDemoCredentials('admin', 'Admin@123')}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1e40af',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Super Admin
                    </button>
                    <button
                      type="button"
                      onClick={() => setDemoCredentials('9454401009', 'District@123')}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1e40af',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      District Admin
                    </button>
                    <button
                      type="button"
                      onClick={() => setDemoCredentials('9454402726', 'Station@123')}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1e40af',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Station User
                    </button>
                  </div>
                </div> */}
              </div>

              <div className="modal-footer" style={{ background: '#f8fafc', padding: '12px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsLoginOpen(false)}
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
